/**
 * A linha do tempo do prontuário: as consultas do paciente que têm registro —
 * ficha preenchida ou resumo aprovado da VecchIA —, da mais recente para a mais
 * antiga.
 *
 * Consulta sem registro não entra: agendamento futuro ou falta não é
 * prontuário. Ficha ou resumo cuja consulta não está na agenda (encaixe sem
 * agendamento) não some: entra com a data em que foi gravado.
 */

import type { ResumoDaConsulta } from './notaDeIa';
import type { FichaPreenchida, ProntuarioDoPaciente } from './tipos';

export interface ConsultaDoProntuario {
    atendimentoId: string;
    rotulo: string;
    fichas: FichaPreenchida[];
    resumos: ResumoDaConsulta[];
}

function dataCurta(iso: string): string {
    return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

export function consultasDoProntuario(prontuario: ProntuarioDoPaciente, resumos: ResumoDaConsulta[]): ConsultaDoProntuario[] {
    const conhecidas = new Set(prontuario.atendimentos.map((a) => a.id));
    const daAgenda = prontuario.atendimentos
        .map((a) => ({
            atendimentoId: a.id,
            rotulo: a.rotulo,
            fichas: prontuario.fichas.filter((f) => f.atendimentoId === a.id),
            resumos: resumos.filter((r) => r.atendimentoId === a.id),
        }))
        .filter((c) => c.fichas.length > 0 || c.resumos.length > 0);

    /* Fora da agenda: agrupa pelo atendimento, com a data do registro mais antigo. */
    const soltas = new Map<string, ConsultaDoProntuario & { quando: string }>();
    const guardar = (atendimentoId: string, quando: string) => {
        const atual = soltas.get(atendimentoId);
        if (!atual) {
            const nova = { atendimentoId, rotulo: `${dataCurta(quando)} · Consulta`, fichas: [], resumos: [], quando };
            soltas.set(atendimentoId, nova);
            return nova;
        }
        if (quando < atual.quando) {
            atual.quando = quando;
            atual.rotulo = `${dataCurta(quando)} · Consulta`;
        }
        return atual;
    };
    for (const f of prontuario.fichas) {
        if (!conhecidas.has(f.atendimentoId)) guardar(f.atendimentoId, f.criadoEm).fichas.push(f);
    }
    for (const r of resumos) {
        if (!conhecidas.has(r.atendimentoId)) guardar(r.atendimentoId, r.aprovadoEm).resumos.push(r);
    }

    const foraDaAgenda = [...soltas.values()]
        .sort((a, b) => b.quando.localeCompare(a.quando))
        .map((c): ConsultaDoProntuario => ({ atendimentoId: c.atendimentoId, rotulo: c.rotulo, fichas: c.fichas, resumos: c.resumos }));
    return [...daAgenda, ...foraDaAgenda];
}

/** O começo do resumo, para a linha. O texto inteiro fica no modal. */
export function trechoDoResumo(resumo: string, limite = 180): string {
    const limpo = resumo.replace(/\s+/g, ' ').trim();
    if (limpo.length <= limite) return limpo;
    const corte = limpo.slice(0, limite);
    const espaco = corte.lastIndexOf(' ');
    return `${(espaco > limite * 0.6 ? corte.slice(0, espaco) : corte).trimEnd()}…`;
}
